'use client';

import { useState } from 'react';

import type { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useCurrencyContext } from '@contexts/currency-context';
import { useTicketsStore } from '@store/tickets-store';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import {
  Button,
  Checkbox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@v2/ui';
import { cn } from '@v2/utils';

import { ZONE_BUCKET_LABELS, type ZoneBucket } from '../utils/ticket-ranking';
import { useTicketFilterModel } from '../utils/use-ticket-filter-model';
import { TicketPriceRangeSlider } from './TicketPriceRangeSlider';

type Props = {
  ticketsList: TicketGroup[];
  className?: string;
  fullWidth?: boolean;
};

export function TicketFiltersDropdown({
  ticketsList,
  className,
  fullWidth,
}: Props) {
  const [open, setOpen] = useState(false);
  const { priceRate, currency } = useCurrencyContext();
  const displayRate =
    Number.isFinite(priceRate) && priceRate > 0 ? priceRate : 1;

  const zoneBuckets = useTicketsStore((s) => s.zoneBuckets);
  const toggleZoneBucket = useTicketsStore((s) => s.toggleZoneBucket);
  const accessibleOnly = useTicketsStore((s) => s.accessibleOnly);
  const underPriceCap = useTicketsStore((s) => s.underPriceCap);
  const setUnderPriceCap = useTicketsStore((s) => s.setUnderPriceCap);
  const toggleBoolean = useTicketsStore((s) => s.toggleBoolean);
  const selectedGroups = useTicketsStore((s) => s.selectedGroups);
  const setSelectedGroups = useTicketsStore((s) => s.setSelectedGroups);
  const isOnlyElectronic = useTicketsStore((s) => s.isOnlyElectronic);

  const { options, activeCount, clearAll } = useTicketFilterModel(ticketsList);

  return (
    <div className={cn(className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className={cn(
              'h-9 gap-x-2 bg-white font-semibold text-gray-800',
              fullWidth && 'w-full'
            )}
          >
            Filters
            {activeCount > 0 && (
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1.5 text-[11px] font-bold text-white">
                {activeCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="flex max-h-[min(80vh,640px)] w-[min(100vw-2rem,360px)] flex-col overflow-hidden p-0"
        >
          <div className="flex shrink-0 items-center justify-between gap-2 px-4 pt-4">
            <p className="text-sm font-bold text-gray-800">Filters</p>
            {activeCount > 0 && (
              <button
                type="button"
                className="text-xs font-semibold text-primary-600"
                onClick={clearAll}
              >
                Clear all
              </button>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 px-4 pt-4">
            <TicketPriceRangeSlider ticketsList={ticketsList} />
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 pb-4 pt-4">
            {options.zones.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Seat location
                </p>
                <div className="space-y-2">
                  {options.zones.map((zone: ZoneBucket) => {
                    const checked = zoneBuckets.includes(zone);
                    return (
                      <label
                        key={zone}
                        className="flex cursor-pointer items-center gap-x-3"
                      >
                        <Checkbox
                          className="rounded-lg bg-white"
                          size="sm"
                          checked={checked}
                          onCheckedChange={() => toggleZoneBucket(zone)}
                        />
                        <span className="text-sm font-medium text-gray-800">
                          {ZONE_BUCKET_LABELS[zone]}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Options
              </p>
              <div className="space-y-2">
                {options.underCap != null && (
                  <label className="flex cursor-pointer items-center gap-x-3">
                    <Checkbox
                      className="rounded-lg bg-white"
                      size="sm"
                      checked={underPriceCap != null}
                      onCheckedChange={() =>
                        setUnderPriceCap(
                          underPriceCap != null ? null : options.underCap
                        )
                      }
                    />
                    <span className="text-sm font-medium text-gray-800">
                      Under{' '}
                      {formatCurrencyV2(
                        options.underCap,
                        displayRate,
                        currency,
                        false,
                        false
                      )}
                    </span>
                  </label>
                )}

                <label className="flex cursor-pointer items-center gap-x-3">
                  <Checkbox
                    className="rounded-lg bg-white"
                    size="sm"
                    checked={isOnlyElectronic}
                    onCheckedChange={() => toggleBoolean('isOnlyElectronic')}
                  />
                  <span className="text-sm font-medium text-gray-800">
                    Mobile / electronic tickets
                  </span>
                </label>

                {options.hasAda && (
                  <label className="flex cursor-pointer items-center gap-x-3">
                    <Checkbox
                      className="rounded-lg bg-white"
                      size="sm"
                      checked={accessibleOnly}
                      onCheckedChange={() => toggleBoolean('accessibleOnly')}
                    />
                    <Label className="cursor-pointer text-sm font-medium text-gray-800">
                      Accessible seats
                    </Label>
                  </label>
                )}

                {selectedGroups.length > 0 && (
                  <button
                    type="button"
                    className="text-left text-sm font-semibold text-gray-600 underline-offset-2 hover:underline"
                    onClick={() => setSelectedGroups([])}
                  >
                    Clear map section selection
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-200 px-4 py-3">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="w-full"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
